import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import Box from '@ff/ui/Box';
import { ZIndexBuilder } from '@ff/ui/utils/ZIndexBuilder';
import Swiper, { SwiperSlide, Props as SwiperProps } from '@ff/ui/Swiper';
import { IS_APP } from 'shared/lib/pathUtils';
import Highlight from './Highlight';
import ShareDrawer from './ShareDrawer';
import type { Props as HighlightsDetailProps } from './highlightsDetailProps';
import setSafeAreaInsetTop from 'shared/lib/setSafeAreaInsetTop';
import Image from '@ff/ui/Image';
import closeSvg from './button_cancel.svg';
import Button from '@ff/ui/Button';
import { useResponsiveDirection } from './useResponsiveDirection';

const zIndices = new ZIndexBuilder({
  shareDrawer: {
    shareIconButton: {
      closeButtonContainer: {
        arrow: {
          swiper: {}
        }
      },
    }
  },
}).zIndices;

export type DirectionProps = 'horizontal' | 'vertical';

export type isRtlLanguage = {
  isRtlLanguage: boolean;
}

export type LayoutProps = 'TextFirst' | 'ImageFirst';

export type SocialFeedEvents = {
  onScrollable?: (e: boolean) => void;
}

export interface Props {
  highlights?: HighlightsDetailProps;
  open?: boolean;
  onClose?: () => void;
}

export default function HighlightsDetail(props: Props) {
  const theme = useTheme();
  const { highlights, open, onClose } = props;
  const [swiper, setSwiper] = useState<Parameters<SwiperProps['onSwiper']>[0]>();
  const [slideIndex, setSlideIndex] = useState(0);
  const [isElementScrollable, setIsElementScrollable] = useState(false);
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);

  const breakpointMd = theme.breakpointValues.md || 900;
  const direction = useResponsiveDirection(breakpointMd);

  const isRtlLanguage = document?.documentElement?.dir === 'rtl';

  const modalContentRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // If modal is closed and ref is current, scroll to the top
      if (modalContentRef.current) {
        modalContentRef.current.scrollTop = 0;
      }

      if (swiper) swiper.slideTo(0); 
    }
  }, [open, swiper]);

  useEffect(() => {
    // Close highlight modal on escape
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (open) onClose();
      }
    }

    window.addEventListener('keyup', handleEsc);

    return () => {
      window.removeEventListener('keyup', handleEsc);
    }
  }, [onClose, open]);

  useEffect(() => {
    if (!swiper) return;
    if (!swiper.isHorizontal()) return;
    // Disable Swiper horizontal scroll if the hovered element is scrollable
    if (isElementScrollable) {
      return swiper.mousewheel.disable();
    }
    if (!isElementScrollable && !swiper.mousewheel.enabled) {
      swiper.mousewheel.enable();
    }
  }, [isElementScrollable, swiper]);

  // Handle swiper controls when opening the social share drawer
  useEffect(() => {
    if (!swiper) return;
    if (isShareDrawerOpen) {
      // For desktop devices
      if (swiper.isHorizontal()) {
        swiper.mousewheel.disable();
        swiper.keyboard.disable();
      }
    } else {
      // Reset the swiper slide movement states
      swiper.keyboard.enable();
      swiper.mousewheel.enable();
    }
  }, [isShareDrawerOpen, swiper]);

  return (
    <Container>
      {/* Close Highlight Detail Modal */}
      <ShareDrawer
        isRtlLanguage={isRtlLanguage}
        socialLinks={highlights?.socialLinks}
        isShareDrawerOpen={isShareDrawerOpen}
        onClose={() => setIsShareDrawerOpen(prevState => !prevState)}
        setIsShareDrawerOpen={setIsShareDrawerOpen}
        zIndices={{
          shareDrawer: zIndices['shareDrawer'],
          shareIconButton: zIndices['shareIconButton'],
        }}
      />
      <CloseButtonContainer isRtlLanguage={isRtlLanguage}>
        <Button onClick={onClose}>
          <Image src={closeSvg} />
        </Button>
      </CloseButtonContainer>
      
      {/* Mobile Vertical Slides */}
      <Box display={{ xs: 'block', md: 'none' }}>
        {highlights && highlights.slides?.map((slide, index) => (
          <StyledSwiperSlide key={index}>
            <Box marginTop='60px'>
              <HighlightWrapper>
                <Highlight
                  isRtlLanguage={isRtlLanguage}
                  onScrollable={(e) => setIsElementScrollable(e)}
                  {...slide}
                />
              </HighlightWrapper>
            </Box>
          </StyledSwiperSlide>
        ))}
      </Box>

      {/* Desktop Swiper */}
      <DesktopStyledSwiper
        className='mySwiper'
        onSwiper={setSwiper}
        onSlideChange={(e) => setSlideIndex(e.activeIndex)}
        autoHeight={true}
        direction={direction}
        freeMode={{
          enabled: true,
          momentum: true,
        }}
        keyboard={{
          enabled: true,
        }}
        loop={false}
        mousewheel={true}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }}
        pagination={false}
        slidesPerView={'auto'}
        speed={400}
      >
        {highlights && highlights.slides?.map((slide, index) => (
          <StyledSwiperSlide key={index}>
            <HighlightWrapper>
              <Highlight
                isRtlLanguage={isRtlLanguage}
                onScrollable={(e) => setIsElementScrollable(e)}
                {...slide}
              />
            </HighlightWrapper>
          </StyledSwiperSlide>
        ))}
        <Controls>
          {/* Hide prev arrow for the first slide */}
          <Arrow
            isHidden={slideIndex === 0}
            onClick={() => swiper?.slidePrev()}
          >
            {isRtlLanguage ? <> &#x2192;</> : <>&#x2190;</>}
          </Arrow>
          {/* Hide next arrow for the last slide */}
          <Arrow
            isHidden={slideIndex === swiper?.slides?.length - 1}
            onClick={() => swiper?.slideNext()}
          >
            {isRtlLanguage ? <> &#x2190;</> : <>&#x2192;</>}
          </Arrow>
        </Controls>
      </DesktopStyledSwiper>
    </Container>
  );
}

const CloseButtonContainer = styled(Box)<{ isRtlLanguage: boolean }>(({ theme, isRtlLanguage }) => ({
  left: isRtlLanguage ? undefined : '10px',
  right: isRtlLanguage ? '10px' : undefined,
  paddingLeft: '10px',
  paddingTop: IS_APP ? 0 : '10px',
  position: 'fixed',
  top: IS_APP ? setSafeAreaInsetTop() : '0.55em',
  zIndex: zIndices['closeButtonContainer'],
  [theme.mediaQueries.md]: {
    alignItems: 'center',
    display: 'flex',
    height: '100%',
    justifyContent: 'center',
    left: 0,
    marginTop: 0,
    position: 'absolute',
    top: 0,
    width: '100px',
  },
}));

const Container = styled(Box)(({ theme }) => ({
  '& .swiper-slide': {
    height: 'auto',
  },
  backgroundColor: theme.colors.plaster,
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  [theme.mediaQueries.md]: {
    '& .swiper-slide': {
      height: '100%',
    },
  }
}));

const HighlightWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: 'auto',
  paddingLeft: '16px',
  paddingRight: '16px',
  paddingTop: '20px',
  paddingBottom: '40px',
  [theme.mediaQueries.md]: {
    flexDirection: 'row',
    height: '100vh',
    paddingBottom: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingTop: 0,
  }
}));

const DesktopStyledSwiper = styled(Swiper)(({ theme }) => ({
  display: 'none',
  paddingTop: '46px !important', // override swiper default
  transform: 'translate3d(0, 0, 0)',
  [theme.mediaQueries.md]: {
    display: 'block',
    minHeight: '100%',
    paddingTop: '0 !important', // override swiper default 
  }
}));

const StyledSwiperSlide = styled(SwiperSlide)(({ theme }) => ({
  maxWidth: '100%',
  zIndex: zIndices['swiper'],
  [theme.mediaQueries.md]: {
    maxWidth: 'auto',
  }
}));

const Controls = styled(Box)(({ theme }) => ({
  display: 'none',
  [theme.mediaQueries.md]: {
    display: 'flex',
    justifyContent: 'space-between',
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: '60px',
    width: '80vw',
    margin: 'auto',
  },
}));

const Arrow = styled(Box)<{ isHidden: boolean }>(({ isHidden }) => ({
  alignItems: 'center',
  background: 'rgba(243, 243, 243, 0.5)',
  border: '1px solid rgba(0,0,0,0.3)',
  borderRadius: '50%',
  boxShadow: 'rgb(187 187 187) 0px 0px 1px',
  cursor: 'pointer',
  display: 'flex',
  fontSize: '19px',
  height: '36px',
  justifyContent: 'center',
  paddingBottom: '1px',
  textShadow: 'rgb(187 187 187) 0px 0px 1px',
  transition: 'background 0.3s ease, color 0.3s ease',
  visibility: isHidden ? 'hidden' : 'visible',
  width: '36px',
  zIndex: zIndices['arrow'],
  '&:hover': {
    background: 'rgb(105, 106, 110)',
    color: 'white',
  }
}));
